import { z } from "zod";

import { attachmentResolver } from "./AttachmentModel";

import { logoResolver } from "./CreateTypes";
import { EntityMessagingConversationRoles } from "./FirebaseUserRole";

export enum conversationsEnum {
	CORE_USER_GENERAL = "conversation.core.user.general",
	CORE_USER_GROUP = "conversation.core.user.group",
	SOCCER_CLUB_GENERAL = "conversation.soccer.club.general",
	SOCCER_CLUB_MANAGERS = "conversation.soccer.club.managers",
	SOCCER_CLUB_MANAGERS_AND_COACHES = "conversation.soccer.club.managersAndCoaches",
	SOCCER_CLUB_MANAGERS_AND_PLAYERS = "conversation.soccer.club.managersAndPlayers",
	SOCCER_CLUB_MANAGERS_AND_TEAM_COACHES_AND_PLAYERS = "conversation.soccer.club.managersAndTeamCoachesAndPlayers",
	SOCCER_GROUP_GENERAL = "conversation.soccer.group.general",
	SOCCER_LEAGUE_GENERAL = "conversation.soccer.league.general",
	SOCCER_LEAGUE_MANAGERS = "conversation.soccer.league.managers",
	SOCCER_TEAM_GENERAL = "conversation.soccer.team.general",
	SOCCER_COMPETITION_GENERAL = "conversation.soccer.competition.general",
	SOCCER_COMPETITION_LEAGUE_AND_CLUB_MANAGERS_AND_TEAMS_COACHES = "conversation.soccer.competition.leagueAndClubManagersAndTeamsCoaches"
}

export type DbEvent =
	| { type: "conversations-changed" }
	| {
			type: "messages-changed";
			conversation?: {
				id: string;
				messages?: DBChatMessage[];
			};
	  }
	| { type: "participants-changed"; participantIds?: string[] }
	| { type: "drafts-changed"; conversationId: string }
	| { type: "activities-changed"; conversationId: string };

export const messagingDBConfig = {
	name: "tapin_sports_messaging_app_db",
	version: 1,
	stores: {
		conversations: {
			name: "conversations"
		},
		messages: {
			name: "messages",
			sortIndex: "conversationId_sentAt_id"
		},
		participants: {
			name: "participants"
		},
		drafts: {
			name: "drafts"
		},
		activities: {
			name: "activities"
		}
	}
};

export const messagingConfig = {
	broadcastChannel: "tapin_sports_messaging_db-events",
	initialWindowSize: 40,
	initialOlderPart: 20,
	initialNewerPart: 20,
	pageSizeOlder: 30,
	pageSizeNewer: 30,
	autoLoadTopThreshold: 80,
	autoLoadBottomThreshold: 120
};

export type MessagesSortKey = [string, number, string];

export type MessagesWindow = {
	items: DBChatMessage[];
	hasOlder: boolean;
	hasNewer: boolean;
	anchor: MessagesSortKey | null;
	fullyLoadedBefore?: boolean;
	fullyLoadedAfter?: boolean;
};

export enum MessageStatuses {
	READ = "read",
	WAITING = "waiting", // TODO: remove ?
	PLACEHOLDER = "_PLACEHOLDER_", // TODO: remove?
	OUTGOING = "outgoing"
}

export enum MessagingActivities {
	TYPING = "typing",
	UPLOADING_ATTACHMENT = "uploadingAttachment"
}

export enum MessagePositions {
	LEFT = "left",
	RIGHT = "right"
}

const timeStamps = z.object({
	seconds: z.number(),
	nanoseconds: z.number(),
	type: z.string().optional()
});

const messageSender = z.object({
	id: z.string(),
	name: z.string(),
	shortName: z.string(),
	avatar: logoResolver.nullable()
});

const chatMessage = z.object({
	id: z.string(),
	tag: z.string(),
	sentAt: timeStamps.or(z.string()),
	removedAt: timeStamps.or(z.string()).nullable().optional(),
	editedAt: timeStamps.or(z.string()).nullable().optional(),
	sender: messageSender,
	body: z.object({
		text: z.string()
	}),
	attachments: z.array(attachmentResolver).optional(),
	tempStatus: z.nativeEnum(MessageStatuses).optional()
});

export const conversationSubjectsResolver = z.object({
	type: z.nativeEnum(conversationsEnum),
	params: z.array(z.string())
});

export const participantResolver = z.object({
	avatar: logoResolver.nullable(),
	id: z.string(),
	name: z.string(),
	shortName: z.string()
});

export const conversationsSettingsResolver = z.object({
	allowMemberInvites: z.boolean()
});

export const conversationsResolver = z.object({
	id: z.string(),
	lastMessage: chatMessage.nullable().optional(),
	roles: z.array(z.string()).optional(),
	subject: conversationSubjectsResolver.optional(),
	muted: z.boolean(),
	pinned: z.boolean(),
	title: z.object({
		text: z.string(),
		image: logoResolver.nullable().optional(),
		isCustom: z.boolean()
	}),
	lastParticipants: z.array(participantResolver),
	lastOpenedAt: z.string().nullable(),
	settings: conversationsSettingsResolver
});

export const chatResolver = conversationsResolver.omit({
	roles: true
});

export const chatDetailsResolver = conversationsResolver
	.omit({
		roles: true
	})
	.extend({
		role: z.nativeEnum(EntityMessagingConversationRoles),
		participantsCount: z.number()
	});

export type ConversationInfo = z.infer<typeof chatResolver>;
export type ConversationsSettings = z.infer<typeof conversationsSettingsResolver>;
export type ConversationDetailsInfo = z.infer<typeof chatDetailsResolver>;
export type ChatModel = z.infer<typeof conversationsResolver>;
export type ChatMessage = z.infer<typeof chatMessage>;
export type MessageSender = z.infer<typeof messageSender>;
export type MessageTimestamp = z.infer<typeof timeStamps>;
export type ConversationSubjects = z.infer<typeof conversationSubjectsResolver>;
export type ConversationParticipant = z.infer<typeof participantResolver>;

export type DBChatModel = Omit<ChatModel, "lastOpenedAt" | "lastMessage"> & {
	unreadCount: number;
	updatedAt: number;
	lastOpenedAt: number;
	lastMessage: null | DBChatMessage;
};

export type DBChatMessage = Omit<ChatMessage, "sentAt" | "removedAt" | "editedAt"> & {
	conversationId: string;
	sentAt: number;
	removedAt: null | number;
	editedAt: null | number;
	status: MessageStatuses;
	position: MessagePositions;
};

export type DBConversationParticipant = ConversationParticipant & {
	updatedAt: number;
};

export type DBChatActivity = {
	id: string;
	conversationId: string;
	person: ConversationParticipant;
	activity: MessagingActivities;
	startAt: number;
	removedAt?: null | number;
};
